<template>
  <div class="page page--step1">
    <div class="container">
      <div class="panel">
        <div class="panel-body">
          <form @submit.prevent="handleSubmit">
            <div class="question-table">
              <div class="question-table-row">
                <div class="question-table-col">
                  <div class="content-1">
                    <h1 class="color-h1 h2">Where's your main problem?</h1>
                    <p class="mt-2 leading-loose">Show us on the model or pick from the drop-down list.</p>

                    <div class="body-part-select--desktop form-group mt-2">
                      <label for="body-part-select" class="sr-only">Body part:</label>

                      <select id="body-part-select" v-model="bodyPartInput" required>
                        <option value="">- Please select -</option>
                        <optgroup v-for="(bodyGroup, bodyGroupKey) in bodyParts" :key="bodyGroupKey" :label="bodyGroup.title">
                          <option v-for="(partName, partKey) in bodyGroup.parts" :key="partKey" :value="partKey">{{ partName }}</option>
                        </optgroup>
                      </select>
                    </div>
                  </div>
                </div>

                <div class="question-table-col">
                  <Human :value="bodyPartInput" @change="updateBodyPart" />
                </div>

                <div class="body-part-select--mobile question-table-col">
                  <div class="form-group mt-2">
                    <label for="body-part-select-mobile" class="sr-only">Body part:</label>

                    <select id="body-part-select-mobile" v-model="bodyPartInput" required>
                      <option value="">- Please select -</option>
                      <optgroup v-for="(bodyGroup, bodyGroupKey) in bodyParts" :key="bodyGroupKey" :label="bodyGroup.title">
                        <option v-for="(partName, partKey) in bodyGroup.parts" :key="partKey" :value="partKey">{{ partName }}</option>
                      </optgroup>
                    </select>
                  </div>
                </div>

                <div class="question-table-col align-bottom">
                  <button id="step-1-submit" type="submit" role="button" class="button mt-1" :class="{'btn-highlight': postingTriageData}" :disabled="!canSubmit">
                    Continue
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {mapState} from "vuex"
import Human from "@/components/Survey/Human"

export default {
  components: {
    Human,
  },

  data() {
    return {
      bodyPartInput: "",
      scrollTimeout: null,
    };
  },

  computed: {
    ...mapState("survey", [
      "bodyParts",
      "answers",
      "postingTriageData",
    ]),

    canSubmit() {
      return this.bodyPartInput !== "" && !this.postingTriageData;
    },
  },

  mounted() {
    this.$gtm.trackView('Step 1', '/step-1');
    this.bodyPartInput = this.answers.bodyPart || "";

    this.setCurrentStep(1);

    window.addEventListener("keydown", this.onKeyDown, false);
    window.addEventListener("keyup", this.onKeyUp, false);

    this.scrollToTop();
  },

  destroyed() {
    window.removeEventListener("keydown", this.onKeyDown);
    window.removeEventListener("keyup", this.onKeyUp);
  },

  methods: {
    /**
     * Prevent browser arrow-key scrolling when navigating left/right.
     *
     * @return {void}
     */
    onKeyDown(e) {
      // Don't interfere with meta keys.
      if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) {
        return;
      }

      if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
        e.preventDefault();
      }
    },

    /**
     * Allow sections to be navigated with the keyboard.
     *
     * @return {void}
     */
    onKeyUp(e) {
      switch (e.key) {
        case "ArrowLeft":
          this.$router.push(this.routes.home);
          break;

        case "ArrowRight":
          this.handleSubmit();
          break;
      }
    },

    /**
     * Handle the form submissions.
     *
     * @return {void}
     */
    handleSubmit() {
      if (!this.bodyPartInput) {
        return;
      }

      if (this.answers.bodyPart && this.bodyPartInput !== this.answers.bodyPart) {
        this.clearAnswers();
      }

      this.setBodyPart(this.bodyPartInput);
      this.scrollToTop();
      this.$router.push(this.routes.step2);
    },

    /**
     * Listen for an updated body part.
     *
     * @param {string} part
     * @return {void}
     */
    updateBodyPart(part) {
      this.bodyPartInput = part;

      if (!this.isScreen('md')) {
        clearTimeout(this.scrollTimeout);

        this.scrollTimeout = setTimeout(() => {
          this.$scrollTo('#step-1-submit');
        }, 500);
      }
    },
  },
};
</script>



<style lang="scss">
.page--step1 {
  @include belowScreen(md) {
    .question-table {
      display: block;
      width: 100%;
      max-width: 300px;
      margin-left: auto;
      margin-right: auto;
    }

    .body-part-select--desktop {
      display: none !important;
    }
  }

  @include screen(md) {
    .body-part-select--mobile {
      display: none !important;
    }

    .question-table {
      display: table;
      width: 100%;
    }

    .question-table-row {
      display: table-row;
      width: 100%;
    }

    .question-table-col {
      display: table-cell;
      width: 33%;
      vertical-align: top;

      &.align-bottom {
        vertical-align: bottom;
      }
    }
  }

  .content-1 {
    display: block;
    max-width: 300px;

    p {
      font-size: 20px;
      line-height: 25px;
    }
  }

  .btn, select {
    display: block;
    width: 100%;
    max-width: 300px;
    margin-left: auto;
    margin-right: auto;
  }
}
</style>
